/* <moses-signature-pad>: a dependency-free drawn-signature canvas for the onboarding page.
   Strokes are kept in CSS pixels and redrawn on every resize, so a phone rotation or the
   keyboard opening never wipes the signature (the bug the previous page worked around). */
(() => {
  const PEN = "#000000";
  const WIDTH = 1.6;
  const MAX_DPR = 2;

  const drawStroke = (ctx, points) => {
    if (!points.length) return;
    ctx.beginPath();
    if (points.length === 1) {
      ctx.arc(points[0].x, points[0].y, WIDTH / 2, 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length - 1; i++) {
      const mid = {
        x: (points[i].x + points[i + 1].x) / 2,
        y: (points[i].y + points[i + 1].y) / 2,
      };
      ctx.quadraticCurveTo(points[i].x, points[i].y, mid.x, mid.y);
    }
    const last = points[points.length - 1];
    ctx.lineTo(last.x, last.y);
    ctx.stroke();
  };
  const style = (ctx) => {
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = WIDTH;
    ctx.strokeStyle = PEN;
    ctx.fillStyle = PEN;
  };

  class MosesSignaturePad extends HTMLElement {
    connectedCallback() {
      if (this.canvas) return;
      this.strokes = [];
      this.current = null;
      this.canvas = document.createElement("canvas");
      this.canvas.setAttribute("aria-hidden", "true");
      this.append(this.canvas);
      this.ctx = this.canvas.getContext("2d");
      this.onDown = (event) => {
        if (event.button !== 0 && event.pointerType === "mouse") return;
        event.preventDefault();
        this.canvas.setPointerCapture(event.pointerId);
        this.current = [this.point(event)];
        this.strokes.push(this.current);
        this.redraw();
      };
      this.onMove = (event) => {
        if (!this.current) return;
        event.preventDefault();
        const points = event.getCoalescedEvents?.() || [event];
        for (const p of points) this.current.push(this.point(p));
        this.redraw();
      };
      this.onUp = (event) => {
        if (!this.current) return;
        event.preventDefault();
        this.current = null;
        this.changed();
      };
      this.canvas.addEventListener("pointerdown", this.onDown);
      this.canvas.addEventListener("pointermove", this.onMove);
      this.canvas.addEventListener("pointerup", this.onUp);
      this.canvas.addEventListener("pointercancel", this.onUp);
      this.observer = new ResizeObserver(() => this.resize());
      this.observer.observe(this);
      this.resize();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
    }
    point(event) {
      const rect = this.canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    }
    get dpr() {
      return Math.min(MAX_DPR, window.devicePixelRatio || 1);
    }
    resize() {
      const rect = this.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const dpr = this.dpr;
      this.canvas.width = Math.round(rect.width * dpr);
      this.canvas.height = Math.round(rect.height * dpr);
      this.redraw();
    }
    redraw() {
      const dpr = this.dpr;
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.scale(dpr, dpr);
      style(this.ctx);
      for (const stroke of this.strokes) drawStroke(this.ctx, stroke);
    }
    changed() {
      this.toggleAttribute("data-signed", !this.isEmpty());
      this.dispatchEvent(new CustomEvent("signature:change", { bubbles: true }));
    }
    isEmpty() {
      return this.strokes.length === 0;
    }
    clear() {
      this.strokes = [];
      this.current = null;
      this.redraw();
      this.changed();
    }
    /** A trimmed PNG data URL of the strokes, or "" when empty. */
    toDataURL() {
      if (this.isEmpty()) return "";
      const pad = 8;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const stroke of this.strokes)
        for (const p of stroke) {
          minX = Math.min(minX, p.x);
          minY = Math.min(minY, p.y);
          maxX = Math.max(maxX, p.x);
          maxY = Math.max(maxY, p.y);
        }
      const width = Math.max(1, maxX - minX) + pad * 2;
      const height = Math.max(1, maxY - minY) + pad * 2;
      const scale = MAX_DPR;
      const out = document.createElement("canvas");
      out.width = Math.ceil(width * scale);
      out.height = Math.ceil(height * scale);
      const ctx = out.getContext("2d");
      ctx.scale(scale, scale);
      ctx.translate(pad - minX, pad - minY);
      style(ctx);
      for (const stroke of this.strokes) drawStroke(ctx, stroke);
      return out.toDataURL("image/png");
    }
  }
  if (!customElements.get("moses-signature-pad"))
    customElements.define("moses-signature-pad", MosesSignaturePad);
})();
